import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useSiteContent } from '../../contexts/SiteContentContext'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`
const BF = 'Anton, sans-serif'
const MONO = 'JetBrains Mono, monospace'
const SG = 'Space Grotesk, sans-serif'

// ─── Small reusable input ───
function Field({ label, value, onChange, multiline = false, placeholder = '' }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-white/35 text-xs tracking-[0.2em] uppercase" style={{ fontFamily: MONO }}>
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-400 rounded px-4 py-3 text-white placeholder-white/20 focus:outline-none transition-colors duration-300 resize-none"
          style={{ fontFamily: SG, fontSize: '15px' }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-400 rounded px-4 py-3 text-white placeholder-white/20 focus:outline-none transition-colors duration-300"
          style={{ fontFamily: SG, fontSize: '15px' }}
        />
      )}
    </div>
  )
}

function ImagePreview({ url }) {
  if (!url) return null
  return (
    <div className="mt-2 rounded overflow-hidden border border-neutral-800" style={{ maxHeight: '120px' }}>
      <img
        src={url}
        alt="Preview"
        className="w-full object-cover"
        style={{ maxHeight: '120px', filter: 'brightness(0.8)' }}
        onError={(e) => { e.target.style.display = 'none' }}
      />
    </div>
  )
}

// ─── Image Upload Field (URL + file upload) ───
function ImageUploadField({ label, value, onChange, token }) {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const inputRef = useRef(null)

  const handleFile = async (file) => {
    if (!file) return
    setUploading(true)
    setUploadError('')
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API}/admin/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || 'Error al subir')
      }
      const data = await res.json()
      const imgUrl = `${API}/images/${data.path}`
      onChange(imgUrl)
    } catch (e) {
      setUploadError(e.message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="text-white/35 text-xs tracking-[0.2em] uppercase" style={{ fontFamily: MONO }}>
        {label}
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... o sube una imagen →"
          className="flex-1 bg-neutral-900 border border-neutral-800 focus:border-amber-400 rounded px-4 py-3 text-white placeholder-white/20 focus:outline-none transition-colors duration-300"
          style={{ fontFamily: SG, fontSize: '15px' }}
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-3 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 hover:border-amber-400 text-amber-400 text-xs tracking-widest uppercase rounded transition-all duration-300 disabled:opacity-40 flex items-center gap-2 whitespace-nowrap"
          style={{ fontFamily: MONO }}
        >
          {uploading ? (
            <span className="animate-pulse">Subiendo...</span>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Subir
            </>
          )}
        </button>
      </div>
      {uploadError && <p className="text-red-400 text-xs" style={{ fontFamily: MONO }}>{uploadError}</p>}
      <ImagePreview url={value} />
    </div>
  )
}

// ─── Hero Tab ───
function HeroTab({ data, onChange }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Línea 1 (blanco)" value={data.line1} onChange={(v) => onChange({ ...data, line1: v })} />
        <Field label="Línea 2 (dorado)" value={data.line2} onChange={(v) => onChange({ ...data, line2: v })} />
      </div>
      <Field label="Subtítulo" value={data.subtitle} onChange={(v) => onChange({ ...data, subtitle: v })} />
      <div className="border-t border-neutral-800 pt-6">
        <h4 className="text-white/40 text-xs tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO }}>Fotos</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Field label="Foto 1 (URL)" value={data.photo1_url} onChange={(v) => onChange({ ...data, photo1_url: v })} placeholder="https://..." />
            <ImagePreview url={data.photo1_url} />
          </div>
          <div>
            <Field label="Foto 2 (URL)" value={data.photo2_url} onChange={(v) => onChange({ ...data, photo2_url: v })} placeholder="https://..." />
            <ImagePreview url={data.photo2_url} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Sobre Mí Tab ───
function SobreMiTab({ data, onChange }) {
  const updateHeading = (i, val) => {
    const h = [...data.headings]
    h[i] = val
    onChange({ ...data, headings: h })
  }
  const updateManifesto = (i, field, val) => {
    const m = data.manifesto.map((item, idx) => idx === i ? { ...item, [field]: val } : item)
    onChange({ ...data, manifesto: m })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="border-b border-neutral-800 pb-6">
        <h4 className="text-white/40 text-xs tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO }}>Titular (3 líneas)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.headings.map((h, i) => (
            <Field key={i} label={`Línea ${i + 1}`} value={h} onChange={(v) => updateHeading(i, v)} />
          ))}
        </div>
      </div>
      <div className="border-b border-neutral-800 pb-6">
        <h4 className="text-white/40 text-xs tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO }}>Bio</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Párrafo 1" value={data.bio1} onChange={(v) => onChange({ ...data, bio1: v })} multiline />
          <Field label="Párrafo 2" value={data.bio2} onChange={(v) => onChange({ ...data, bio2: v })} multiline />
        </div>
      </div>
      <div>
        <h4 className="text-white/40 text-xs tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO }}>Manifiesto</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data.manifesto.map((item, i) => (
            <div key={i} className="bg-neutral-900/50 rounded p-4 border border-neutral-800">
              <div className="text-amber-400 text-xs mb-3" style={{ fontFamily: MONO }}>{item.num}</div>
              <Field label="Título" value={item.title} onChange={(v) => updateManifesto(i, 'title', v)} />
              <div className="mt-3">
                <Field label="Descripción" value={item.desc} onChange={(v) => updateManifesto(i, 'desc', v)} multiline />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Projects Tab ───
function ProjectsTab({ data, onChange, token }) {
  const [expandedId, setExpandedId] = useState(null)

  const updateProject = (i, field, val) => {
    const p = data.map((item, idx) => idx === i ? { ...item, [field]: val } : item)
    onChange(p)
  }
  const updateTech = (i, techIndex, val) => {
    const p = data.map((item, idx) => {
      if (idx !== i) return item
      const tech = [...item.tech]
      tech[techIndex] = val
      return { ...item, tech }
    })
    onChange(p)
  }
  const addTech = (i) => {
    const p = data.map((item, idx) => idx === i ? { ...item, tech: [...item.tech, ''] } : item)
    onChange(p)
  }
  const removeTech = (i, techIndex) => {
    const p = data.map((item, idx) => {
      if (idx !== i) return item
      return { ...item, tech: item.tech.filter((_, ti) => ti !== techIndex) }
    })
    onChange(p)
  }
  const addProject = () => {
    const newProject = {
      id: `proyecto-${Date.now()}`,
      num: `0${data.length + 1}`,
      title: 'NUEVO PROYECTO',
      category: 'Diseño Web',
      subcategory: 'Descripción',
      year: new Date().getFullYear().toString(),
      description: '',
      long_description: '',
      tech: [''],
      image: '',
      modal_image: '',
      no_url: true,
    }
    onChange([...data, newProject])
    setExpandedId(newProject.id)
  }
  const removeProject = (i) => {
    onChange(data.filter((_, idx) => idx !== i))
  }

  return (
    <div className="flex flex-col gap-4">
      {data.map((project, i) => (
        <div key={project.id} className="border border-neutral-800 rounded overflow-hidden">
          <button
            onClick={() => setExpandedId(expandedId === project.id ? null : project.id)}
            className="w-full flex items-center justify-between px-5 py-4 bg-neutral-900/50 hover:bg-neutral-900 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <span className="text-amber-400" style={{ fontFamily: MONO, fontSize: '11px' }}>{project.num}</span>
              <span className="text-white font-medium" style={{ fontFamily: BF, fontSize: '18px' }}>{project.title}</span>
              <span className="text-white/30 text-xs" style={{ fontFamily: MONO }}>{project.category}</span>
            </div>
            <span className="text-white/30 text-sm">{expandedId === project.id ? '▲' : '▼'}</span>
          </button>

          <AnimatePresence>
            {expandedId === project.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-5 flex flex-col gap-5 border-t border-neutral-800">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field label="ID (único)" value={project.id} onChange={(v) => updateProject(i, 'id', v)} />
                    <Field label="Número" value={project.num} onChange={(v) => updateProject(i, 'num', v)} />
                    <Field label="Año" value={project.year} onChange={(v) => updateProject(i, 'year', v)} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Título" value={project.title} onChange={(v) => updateProject(i, 'title', v)} />
                    <Field label="Categoría" value={project.category} onChange={(v) => updateProject(i, 'category', v)} />
                  </div>
                  <Field label="Subcategoría" value={project.subcategory} onChange={(v) => updateProject(i, 'subcategory', v)} />

                  {/* URL del proyecto live */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Field label='URL "Ver proyecto live"' value={project.url || ''} onChange={(v) => updateProject(i, 'url', v)} placeholder="https://..." />
                    </div>
                    <div className="flex items-end pb-0">
                      <div className="flex items-center gap-3 pb-3">
                        <input
                          type="checkbox"
                          id={`nurl2-${i}`}
                          checked={project.no_url || false}
                          onChange={(e) => updateProject(i, 'no_url', e.target.checked)}
                          className="w-4 h-4 accent-amber-400"
                        />
                        <label htmlFor={`nurl2-${i}`} className="text-white/50 text-sm" style={{ fontFamily: SG }}>
                          Ocultar botón "Ver proyecto live"
                        </label>
                      </div>
                    </div>
                  </div>

                  <Field label="Descripción corta" value={project.description} onChange={(v) => updateProject(i, 'description', v)} multiline />
                  <Field label="Descripción larga" value={project.long_description} onChange={(v) => updateProject(i, 'long_description', v)} multiline />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ImageUploadField label="Imagen principal" value={project.image} onChange={(v) => updateProject(i, 'image', v)} token={token} />
                    <ImageUploadField label="Imagen modal" value={project.modal_image} onChange={(v) => updateProject(i, 'modal_image', v)} token={token} />
                  </div>

                  {/* Tech stack */}
                  <div>
                    <div className="text-white/35 text-xs tracking-[0.2em] uppercase mb-3" style={{ fontFamily: MONO }}>Tecnologías</div>
                    <div className="flex flex-col gap-2">
                      {project.tech.map((t, ti) => (
                        <div key={ti} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={t}
                            onChange={(e) => updateTech(i, ti, e.target.value)}
                            className="flex-1 bg-neutral-900 border border-neutral-800 focus:border-amber-400 rounded px-3 py-2 text-white focus:outline-none transition-colors"
                            style={{ fontFamily: SG, fontSize: '14px' }}
                          />
                          <button
                            onClick={() => removeTech(i, ti)}
                            className="text-red-400/60 hover:text-red-400 transition-colors px-2 py-1 text-sm bg-transparent border-0"
                          >×</button>
                        </div>
                      ))}
                      <button
                        onClick={() => addTech(i)}
                        className="self-start text-amber-400/60 hover:text-amber-400 transition-colors text-xs tracking-wider uppercase mt-1 bg-transparent border-0"
                        style={{ fontFamily: MONO }}
                      >+ Añadir tecnología</button>
                    </div>
                  </div>

                  <div className="border-t border-neutral-800 pt-4">
                    <button
                      onClick={() => removeProject(i)}
                      className="text-red-400/50 hover:text-red-400 transition-colors text-xs tracking-wider uppercase bg-transparent border-0"
                      style={{ fontFamily: MONO }}
                    >
                      Eliminar proyecto
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}

      <button
        onClick={addProject}
        className="flex items-center gap-3 text-amber-400/70 hover:text-amber-400 transition-colors text-xs tracking-wider uppercase mt-2 bg-transparent border border-dashed border-amber-400/30 hover:border-amber-400/60 rounded px-5 py-3"
        style={{ fontFamily: MONO }}
      >
        + Añadir nuevo proyecto
      </button>
    </div>
  )
}

// ─── Main Dashboard ───
export default function AdminDashboard() {
  const navigate = useNavigate()
  const { content, saveContent } = useSiteContent()
  const [localContent, setLocalContent] = useState(null)
  const [activeTab, setActiveTab] = useState('hero')
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState('')
  const [resetting, setResetting] = useState(false)

  const token = localStorage.getItem('admin_token')

  useEffect(() => {
    if (!token) { navigate('/admin'); return }
    fetch(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) { localStorage.removeItem('admin_token'); navigate('/admin') } })
      .catch(() => { navigate('/admin') })
  }, [navigate, token])

  useEffect(() => {
    if (content) setLocalContent(JSON.parse(JSON.stringify(content)))
  }, [content])

  const handleSave = async () => {
    setSaving(true)
    setSavedMsg('')
    try {
      await saveContent(localContent, token)
      setSavedMsg('Guardado correctamente')
      setTimeout(() => setSavedMsg(''), 3000)
    } catch (err) {
      setSavedMsg('Error al guardar')
      setTimeout(() => setSavedMsg(''), 4000)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!window.confirm('¿Restaurar todo el contenido a los valores por defecto? Esta acción no se puede deshacer.')) return
    setResetting(true)
    try {
      await fetch(`${API}/admin/content/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      window.location.reload()
    } catch {
      setResetting(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin')
  }

  if (!localContent) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-amber-400" style={{ fontFamily: MONO, fontSize: '12px', letterSpacing: '0.3em' }}>
          CARGANDO...
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'hero', label: 'Hero' },
    { id: 'sobre_mi', label: 'Sobre mí' },
    { id: 'projects', label: 'Proyectos' },
  ]

  return (
    <div className="min-h-screen bg-black" style={{ fontFamily: SG }}>
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur border-b border-neutral-900 px-5 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span style={{ fontFamily: BF, fontSize: '24px', color: '#F5A623' }}>JO.</span>
          <span className="text-white/30 text-xs tracking-[0.2em] uppercase" style={{ fontFamily: MONO }}>
            Panel Admin
          </span>
        </div>
        <div className="flex items-center gap-4">
          {savedMsg && (
            <motion.span
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              className={`text-xs tracking-wider ${savedMsg.includes('Error') ? 'text-red-400' : 'text-green-400'}`}
              style={{ fontFamily: MONO }}
            >
              {savedMsg}
            </motion.span>
          )}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/30 hover:text-white transition-colors text-xs tracking-[0.2em] uppercase"
            style={{ fontFamily: MONO }}
          >
            Ver web ↗
          </a>
          <button
            onClick={handleLogout}
            className="text-white/30 hover:text-red-400 transition-colors text-xs tracking-[0.2em] uppercase bg-transparent border-0"
            style={{ fontFamily: MONO }}
          >
            Salir
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 md:px-10 py-10">
        {/* Tab navigation */}
        <div className="flex items-center gap-0 mb-10 border-b border-neutral-900">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-xs tracking-[0.2em] uppercase transition-colors duration-300 bg-transparent border-0 border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-amber-400 border-amber-400'
                  : 'text-white/30 border-transparent hover:text-white/60'
              }`}
              style={{ fontFamily: MONO }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'hero' && (
              <HeroTab
                data={localContent.hero}
                onChange={(h) => setLocalContent({ ...localContent, hero: h })}
              />
            )}
            {activeTab === 'sobre_mi' && (
              <SobreMiTab
                data={localContent.sobre_mi}
                onChange={(s) => setLocalContent({ ...localContent, sobre_mi: s })}
              />
            )}
            {activeTab === 'projects' && (
              <ProjectsTab
                data={localContent.projects}
                onChange={(p) => setLocalContent({ ...localContent, projects: p })}
                token={token}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Save / Reset footer */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t border-neutral-900">
          <button
            onClick={handleReset}
            disabled={resetting}
            className="text-white/20 hover:text-red-400 transition-colors text-xs tracking-wider uppercase bg-transparent border-0 disabled:opacity-40"
            style={{ fontFamily: MONO }}
          >
            {resetting ? 'Restaurando...' : 'Restaurar valores por defecto'}
          </button>
          <motion.button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-save-button"
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-3 text-amber-400 hover:text-white transition-colors duration-300 disabled:opacity-40 bg-transparent border-0"
            style={{ fontFamily: MONO, fontSize: '13px', letterSpacing: '0.25em' }}
          >
            <span className="uppercase">{saving ? 'Guardando...' : 'Guardar cambios'}</span>
            <span className="text-xl">↗</span>
          </motion.button>
        </div>
      </div>
    </div>
  )
}
